import { exhaustiveGuard } from '@libs/utils';

import type { ItineraryAccommodationSegment } from './accommodation';
import type { ItineraryActivitySegment } from './activity';
import type { ItineraryDateLocationSegment } from './dateLocation';
import type {
  AvailableItineraryFerrySegment,
  ItineraryFerrySegment,
  UnavailableItineraryFerrySegment,
} from './ferry';
import type {
  AlternativeItineraryFlightSegment,
  AvailableItineraryFlightSegment,
  ItineraryFlightSegment,
  UnavailableItineraryFlightSegment,
} from './flight';
import { ItinerarySegmentType } from './itinerarySegmentType';
import type { ItineraryLoaderSegment } from './loaderSegments';
import type {
  ItineraryEndLocationSegment,
  ItineraryStartLocationSegment,
} from './location';
import type {
  ItineraryNoTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
} from './noTransport';
import type { ItineraryOwnStaySegment } from './ownStay';
import type {
  ItineraryCarSegment,
  ItineraryCycleSegment,
  ItineraryWalkSegment,
} from './simpleTransport';
import type { ItineraryUpsellSegment } from './upsell';

export type ItinerarySimpleTransportSegment =
  | ItineraryCarSegment
  | ItineraryCycleSegment
  | ItineraryWalkSegment;

export type ItineraryNoTransportRelatedSegment =
  | ItineraryNoTransportSegment
  | ItineraryUpsellNoTransportSegment
  | ItineraryStenaUpsellNoTransportSegment;

export type ItineraryOwnTransportSegment =
  | ItinerarySimpleTransportSegment
  | ItineraryNoTransportRelatedSegment;

export type ItineraryLocationSegment =
  | ItineraryStartLocationSegment
  | ItineraryEndLocationSegment;

export type ItineraryBookableTransportSegment =
  | AvailableItineraryBookableTransportSegment
  | AlternativeItineraryFlightSegment
  | UnavailableItineraryBookableTransportSegment;

export type AvailableItineraryBookableTransportSegment =
  | AvailableItineraryFlightSegment
  | AvailableItineraryFerrySegment;

export type UnavailableItineraryBookableTransportSegment =
  | UnavailableItineraryFlightSegment
  | UnavailableItineraryFerrySegment;

export type ItinerarySegment =
  | ItineraryLocationSegment
  | ItinerarySimpleTransportSegment
  | ItineraryFlightSegment
  | ItineraryAccommodationSegment
  | ItineraryOwnStaySegment
  | ItineraryActivitySegment
  | ItineraryFerrySegment
  | ItineraryUpsellSegment
  | ItineraryNoTransportSegment
  | ItineraryUpsellNoTransportSegment
  | ItineraryStenaUpsellNoTransportSegment
  | ItineraryDateLocationSegment;

export type ItinerarySimpleTransportSegmentType =
  | ItinerarySegmentType.Car
  | ItinerarySegmentType.Cycle
  | ItinerarySegmentType.Walk;

export type ItineraryBookableTransportSegmentType =
  | ItinerarySegmentType.Flight
  | ItinerarySegmentType.Ferry;

export type ItineraryLocationSegmentType =
  | ItinerarySegmentType.StartLocation
  | ItinerarySegmentType.EndLocation;

export type ItinerartBookableSegmentType =
  | ItinerarySegmentType.Accommodation
  | ItinerarySegmentType.Activity
  | ItineraryBookableTransportSegmentType;

export function isItineraryBookableTransport(
  segment: ItinerarySegment | ItineraryLoaderSegment
): segment is ItineraryBookableTransportSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Ferry:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItinerarySimpleTransportSegment(
  segment: ItinerarySegment | ItineraryLoaderSegment
): segment is ItinerarySimpleTransportSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.EndLocation:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryLocationSegment(
  segment: ItinerarySegment
): segment is ItineraryLocationSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryDateLocationSegment(
  segment: ItinerarySegment
): segment is ItineraryDateLocationSegment {
  switch (segment.segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
      return false;

    case ItinerarySegmentType.DateLocation:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isItineraryLocationSegmentType(
  segmentType: ItinerarySegmentType
): segmentType is ItineraryLocationSegmentType {
  switch (segmentType) {
    case ItinerarySegmentType.Accommodation:
    case ItinerarySegmentType.OwnStay:
    case ItinerarySegmentType.Activity:
    case ItinerarySegmentType.Ferry:
    case ItinerarySegmentType.Flight:
    case ItinerarySegmentType.Car:
    case ItinerarySegmentType.Cycle:
    case ItinerarySegmentType.Walk:
    case ItinerarySegmentType.Upsell:
    case ItinerarySegmentType.NoTransport:
    case ItinerarySegmentType.UpsellNoTransport:
    case ItinerarySegmentType.StenaUpsellNoTransport:
    case ItinerarySegmentType.DateLocation:
      return false;

    case ItinerarySegmentType.StartLocation:
    case ItinerarySegmentType.EndLocation:
      return true;

    default:
      exhaustiveGuard(segmentType, 'Not known segment type');
  }
}
